import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { IoAdd } from "react-icons/io5";
import { mdiRemoteTv } from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { deviceIcon } from "../../components/device/app/deviceTheme";
import { useToggleTop } from "../../components/common/ToggleTop";
import { usePropertyRole } from "../../hooks/usePropertyOwner";
import usePropertyDevices from "../../hooks/usePropertyDevices";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import RemoteCard from "./RemoteCard";
import EditRemoteDialog from "./EditRemoteDialog";
import { REMOTE_PATHS, asList, blastersOf, placeOf, remoteNameOf, remotesIn } from "./remoteUtils";
import "../../assets/styles/app-remotes.css";

// The app's Universal Remotes (app/screens/HashhRemote/RemoteListingScreen.js),
// from the "+" action menu: every remote on the home's IR blasters, each
// opening its keys, with an edit button, and "+" to add one. As in the app,
// the IRDB categories load here and go along to the add-remote steps. Only
// owners add and edit remotes, as the app's role checks allow.
function UniversalRemotes() {
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const role = usePropertyRole();
  const { devices, error, reload } = usePropertyDevices();
  // null while loading, false when they couldn't be loaded.
  const [categories, setCategories] = useState(null);
  const [editing, setEditing] = useState(null);

  const loadCategories = useCallback(() => {
    setCategories(null);
    UserService.getRemoteCategories()
      .then((response) => alive.current && setCategories(asList(response) ?? false))
      .catch(() => alive.current && setCategories(false));
  }, [alive]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const blasters = useMemo(() => blastersOf(devices), [devices]);
  const remotes = useMemo(() => remotesIn(blasters), [blasters]);

  // "+" and Add remote: the blaster to learn it on (skipped when the home has
  // one, which the app's Select Device would list alone), then its category.
  const startAdd = () => {
    if (role === "guest") {
      toast("You have to be a owner to add a remote.", "red");
      return;
    }
    if (!devices) return;
    if (!blasters.length) {
      toast("Set up an IR blaster with the HASHh Connect app first.", "blue");
      return;
    }
    if (categories === null) {
      toast("Loading the remote categories…", "blue");
      return;
    }
    if (!categories.length) {
      toast("Couldn't load the remote categories. Please try again.", "red");
      loadCategories();
      return;
    }
    if (blasters.length > 1) {
      history.push(REMOTE_PATHS.ADD_BLASTER, { blasters, categories, steps: 1 });
    } else {
      history.push(REMOTE_PATHS.ADD_CATEGORY, { blaster: blasters[0], categories, steps: 1 });
    }
  };

  const edit = (item) => {
    if (role === "guest") {
      toast("You have to be a owner to edit the device.", "red");
      return;
    }
    setEditing(item);
  };

  let content;
  if (error) {
    content = (
      <div className="hx-empty" role="alert">
        Couldn't load the remotes. Check your connection and try again.
        <button type="button" className="hx-btn" onClick={reload}>
          Try again
        </button>
      </div>
    );
  } else if (!devices) {
    content = (
      <div className="hx-remote-cards" aria-label="Loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="hx-skel hx-remote-card-skel" />
        ))}
      </div>
    );
  } else if (remotes.length) {
    content = (
      <ul className="hx-remote-cards">
        {remotes.map(({ blaster, remote }) => {
          const name = remoteNameOf(remote);
          return (
            <RemoteCard
              key={remote.remoteId}
              icon={deviceIcon(remote.deviceType)}
              name={name}
              accent={remote.brand}
              meta={placeOf(blaster)}
              onOpen={() => history.push(REMOTE_PATHS.VIEW, { data: blaster, remote })}
              onEdit={() => edit({ blaster, remote })}
              editLabel={`Edit ${name}`}
            />
          );
        })}
      </ul>
    );
  } else {
    content = (
      <div className="hx-empty-page">
        <MdiIcon path={mdiRemoteTv} size={120} />
        <p>No remotes added</p>
        {blasters.length ? (
          <>
            <span>Add the remotes of your TV, AC, set-top box and more to use them here.</span>
            <div className="hx-empty-actions">
              <button type="button" className="hx-btn" disabled={categories === null} onClick={startAdd}>
                Add remote
              </button>
            </div>
          </>
        ) : (
          <span>Set up an IR blaster with the HASHh Connect app, then add its remotes here.</span>
        )}
      </div>
    );
  }

  return (
    <Main
      header="stack"
      title="Universal Remotes"
      hideTabBar
      headerRight={
        <button type="button" className="hx-icon-btn" onClick={startAdd} aria-label="Add remote">
          <IoAdd />
        </button>
      }
    >
      {content}
      <EditRemoteDialog
        open={Boolean(editing)}
        blaster={editing?.blaster}
        remote={editing?.remote}
        onClose={() => setEditing(null)}
        onRenamed={reload}
        onDeleted={reload}
      />
    </Main>
  );
}

export default UniversalRemotes;
